import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '@/lib/api';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import OutageNotice from '@/components/OutageNotice';
import { OUTAGE_MODE } from '@/config/outage';
import { isPromoActive } from '@/lib/promoConfig';

function formatApiError(detail) {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  return String(detail);
}

const STEPS = ['Account', 'Mahjong Profile', 'Meetup Preferences', 'Location', 'Alerts', 'Choose Plan'];

// Common email-domain typos -> the domain the user almost certainly meant.
// Order is irrelevant; we match by `endsWith(@<bad>)` so partial matches
// like `gmail.co` vs `gmail.com` don't accidentally trigger.
const EMAIL_DOMAIN_TYPOS = {
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gamil.com': 'gmail.com',
  'yahooo.com': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'hotmial.com': 'hotmail.com',
  'hotmai.com': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outloo.com': 'outlook.com',
  'outlook.con': 'outlook.com',
  'icould.com': 'icloud.com',
  'iclod.com': 'icloud.com',
  'icoud.com': 'icloud.com',
  'icloud.con': 'icloud.com',
  'aol.con': 'aol.com',
};

// Returns a suggested correction (e.g. "jane@gmail.com") if the user's
// address ends in a known-typo domain. Returns null otherwise.
function suggestEmailCorrection(email) {
  const e = (email || '').trim().toLowerCase();
  const at = e.lastIndexOf('@');
  if (at < 1) return null;
  const domain = e.slice(at + 1);
  const fix = EMAIL_DOMAIN_TYPOS[domain];
  if (!fix || fix === domain) return null;
  return e.slice(0, at + 1) + fix;
}

const SKILL_LEVELS = [
  { value: 'newbie', label: 'Newbie', desc: 'I am brand new to the game' },
  { value: 'beginner', label: 'Beginner', desc: 'I have played a few games' },
  { value: 'intermediate', label: 'Intermediate', desc: 'I play regularly and know the rules well' },
  { value: 'advanced', label: 'Advanced', desc: 'I am an experienced player' },
];

const SET_OPTIONS = [
  { value: 'yes', label: 'Yes, I have a set' },
  { value: 'no', label: "No, I don't have a set" },
  { value: 'sometimes', label: 'Sometimes (I can occasionally bring one)' },
];

const MEETUP_TYPES = [
  { value: 'public', label: 'Public meetups only', desc: 'Coffee shops, libraries, community centers, etc.' },
  { value: 'public_and_private', label: 'Public and private', desc: 'Including homes' },
];

const GENDER_PREFS = [
  { value: 'no_preference', label: 'No preference' },
  { value: 'women_only', label: 'Women only' },
  { value: 'men_only', label: 'Men only' },
  { value: 'mixed', label: 'Mixed preferred' },
];

const DISTANCES = ['5', '10', '15', '25', '50', '50+'];
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const TIMES = [
  { value: 'morning', label: 'Morning (before noon)' },
  { value: 'afternoon', label: 'Afternoon (noon - 5pm)' },
  { value: 'evening', label: 'Evening (after 5pm)' },
  { value: 'flexible', label: 'Flexible' },
];

const ALERTS = [
  { value: 'email', label: 'Email alerts' },
  { value: 'manual', label: "I'll check the site manually" },
];

const PLANS = [
  { id: 'free', name: 'Open Play', price: '$0', period: 'forever', color: '#64748b' },
  { id: 'verified_player_monthly', name: 'Verified Player · Monthly', price: '$0.99', period: '/month', color: '#56c6df' },
  { id: 'verified_player_annual', name: 'Verified Player · Annual', price: '$9.99', period: '/year', color: '#56dfb4', best: true },
];

// Instructor-side plans. Step 5 shows this set instead of PLANS when the
// user ticked the "I'm an instructor" box upstream. Keeps instructor
// signups from accidentally landing on a player-only plan that excludes
// event-posting and the Premium Instructor badge.
const INSTRUCTOR_PLANS = [
  { id: 'free', name: 'Instructor Starter', price: '$0', period: 'forever', color: '#64748b' },
  { id: 'instructor_plus_monthly', name: 'Instructor Advantage · Monthly', price: '$9.99', period: '/month', color: '#56c6df' },
  { id: 'instructor_plus_annual', name: 'Instructor Advantage · Annual', price: '$99.99', period: '/year', color: '#56dfb4', best: true },
  { id: 'instructor_unlimited_monthly', name: 'Instructor Unlimited · Monthly', price: '$29.99', period: '/month', color: '#b456df' },
  { id: 'instructor_unlimited_annual', name: 'Instructor Unlimited · Annual', price: '$299.99', period: '/year', color: '#b456df' },
];

// IDs that belong to the PLAYER track — used to detect when an instructor
// user is about to pick a player plan and trigger a confirmation modal.
const PLAYER_PLAN_IDS = new Set(['verified_player_monthly', 'verified_player_annual']);

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Phase 2: PricingPage CTAs deep-link to /register?tier=<tier>&cadence=<cadence>.
  // Preserve these through signup so the user lands on /subscription with
  // the right plan pre-selected (instead of getting punted to dashboard).
  const queryTier = searchParams.get('tier') || '';
  const queryCadence = searchParams.get('cadence') || '';
  // Pre-tick "I teach Mahjong" when arriving from an instructor-tier
  // pricing CTA (?tier=instructor_*) or explicit persona hint
  // (?persona=instructor). The PricingPage persona toggle uses both
  // signals — keeps the signup flow's plan track aligned with whatever
  // they were shown on /pricing without forcing a re-classification.
  const personaQuery = (searchParams.get('persona') || '').toLowerCase();
  const prefillIsInstructor = (
    queryTier.startsWith('instructor_') || personaQuery === 'instructor'
  );
  const [step, setStep] = useState(0);
  // MFMFREE3 (Feb 2026) promo has been retired. The state below preserves
  // the shape used by the rest of the file but is now permanently false —
  // the free-trial hero cards and free-trap modal no longer surface.
  const applyMfmfree3 = false;
  const setApplyMfmfree3 = () => { /* MFMFREE3 promo retired — no-op */ };
  // Controls whether the full plans grid is revealed. With the promo
  // retired we always reveal the grid up front (the old "single CTA hero"
  // path only made sense when the trial was on offer).
  const [showAllPlans, setShowAllPlans] = useState(true);
  // Exit-intent modal that previously fired when users opted out of the
  // MFMFREE3 trial. Kept for layout compatibility but never opens.
  const [freeTrapOpen, setFreeTrapOpen] = useState(false);
  // Fires when an instructor user (form.is_instructor=true) tries to commit
  // to a player-only plan (Open Play or Verified Player) — last-second
  // nudge to keep them on an instructor tier.
  const [instructorMismatchOpen, setInstructorMismatchOpen] = useState(false);
  // Stores the player plan id the instructor user was about to pick, so
  // we can complete the selection if they confirm "yes, I really want it".
  const [pendingPlayerPlan, setPendingPlayerPlan] = useState(null);
  // Reveals the player plans grid for instructors who want to peek. Stored
  // separately from `form` so it isn't sent to the backend.
  const [showPlayerPlansForInstructor, setShowPlayerPlansForInstructor] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    first_name: '', last_name: '', display_name: '', email: '', confirm_email: '', password: '', confirm_password: '', phone: '',
    gender: '',
    skill_level: 'beginner', has_mahjong_set: 'no', meetup_type: 'public', gender_preference: 'no_preference',
    travel_distance: '10', zip_code: '', days_available: [], day_times: {}, time_preferences: [], alert_preferences: ['email'],
    last_minute_alerts: false,
    sms_consent: false,
    selected_plan: 'free', agreed_to_terms: false,
    is_instructor: prefillIsInstructor, instructor_public: false, instructor_bio: '', instructor_lesson_types: [],
    social_facebook: '', social_instagram: '', social_twitter: '', social_linkedin: '',
    referral_code: new URLSearchParams(window.location.search).get('ref') || '',
  });

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const toggleArray = (key, value) => {
    setForm(prev => ({
      ...prev,
      [key]: prev[key].includes(value) ? prev[key].filter(v => v !== value) : [...prev[key], value],
    }));
  };

  const validateStep = () => {
    setError('');
    if (step === 0) {
      if (!form.first_name || !form.last_name || !form.display_name || !form.email || !form.password) {
        setError('Please fill in all required fields'); return false;
      }
      // Light, defensive email format check — backend re-validates.
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
        setError('That doesn\'t look like a valid email — please double-check it.');
        return false;
      }
      if (form.email.trim().toLowerCase() !== form.confirm_email.trim().toLowerCase()) {
        setError('Email addresses don\'t match — please re-enter your email below to confirm.'); return false;
      }
      if (form.password.length < 6) { setError('Password must be at least 6 characters'); return false; }
      if (form.password !== form.confirm_password) { setError('Passwords do not match'); return false; }
    }
    if (step === 3 && !form.zip_code) { setError('Please enter your zip code'); return false; }
    if (step === 4 && !form.agreed_to_terms) {
      setError('Please agree to the Terms & Conditions and Privacy Policy before continuing.');
      // Scroll the Terms checkbox into view so users see exactly what's blocking them.
      setTimeout(() => {
        try {
          document.querySelector('[data-testid="reg-terms-checkbox"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch { /* ignore */ }
      }, 50);
      return false;
    }
    return true;
  };

  const nextStep = () => { if (validateStep()) setStep(s => Math.min(s + 1, 5)); };
  const prevStep = () => setStep(s => Math.max(s - 1, 0));

  const handleRegister = async () => {
    if (!form.agreed_to_terms) {
      setError('Please check the Terms agreement below to continue.');
      // Scroll the error banner (at the top of the card) into view so users
      // on the Choose Plan step see exactly what's wrong without scrolling
      // back to Step 4 to hunt for the checkbox.
      setTimeout(() => {
        try {
          document.querySelector('[data-testid="register-error"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          document.querySelector('[data-testid="reg-terms-checkbox-plan-step-wrapper"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch { /* ignore */ }
      }, 50);
      return;
    }
    setLoading(true);
    setError('');
    try {
      // SMS opt-in is source-of-truth: only include "sms" in alert_preferences
      // when the user gave explicit consent AND provided a phone number. This
      // is what Twilio / CTIA compliance reviewers expect.
      const smsAllowed = form.sms_consent && !!(form.phone && form.phone.trim());
      const finalAlertPrefs = Array.from(new Set([
        ...form.alert_preferences.filter(p => p !== 'sms'),
        ...(smsAllowed ? ['sms'] : []),
      ]));
      const regData = {
        first_name: form.first_name, last_name: form.last_name, display_name: form.display_name,
        email: form.email, password: form.password, phone: form.phone, gender: form.gender,
        skill_level: form.skill_level, has_mahjong_set: form.has_mahjong_set,
        meetup_type: form.meetup_type, gender_preference: form.gender_preference,
        travel_distance: form.travel_distance, zip_code: form.zip_code,
        days_available: form.days_available, day_times: form.day_times,
        time_preferences: form.time_preferences,
        alert_preferences: finalAlertPrefs, last_minute_alerts: form.last_minute_alerts,
        sms_consent: smsAllowed,
        agreed_to_terms: form.agreed_to_terms,
        is_instructor: form.is_instructor, instructor_public: form.instructor_public,
        instructor_bio: form.instructor_bio, instructor_lesson_types: form.instructor_lesson_types,
        social_facebook: form.social_facebook, social_instagram: form.social_instagram,
        social_twitter: form.social_twitter, social_linkedin: form.social_linkedin,
        referral_code: form.referral_code,
      };
      await register(regData);

      // Google Ads conversion tracking — fires the "MFM Sign-up"
      // conversion immediately after the registration POST succeeds,
      // regardless of which tier the user lands on next. The window
      // check + try/catch ensures ad-blockers or a failed gtag load
      // never break the signup happy path.
      try {
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            send_to: 'AW-18257686428/kGcdCMfLwsMcEJzf-IFE',
          });
        }
      } catch (_e) { /* never block signup on analytics */ }

      // Phase 2 deep-link from PricingPage: ?tier=&cadence= takes
      // precedence over the in-page Choose Plan step, since the user
      // already made a choice on the public Pricing page. Send them
      // straight to /subscription with both query params preserved.
      // MFMFREE3 auto-applies for Verified Player AND Instructor
      // Advantage (`instructor_plus`) — the backend allowlist matches.
      // It does NOT apply to Instructor Unlimited.
      const PAID_TIERS = ['verified_player', 'instructor_plus', 'instructor_unlimited'];
      const MFMFREE3_ELIGIBLE_TIERS = ['verified_player', 'instructor_plus'];
      const MFMFREE3_ELIGIBLE_PLAN_IDS = new Set([
        'verified_player_monthly', 'verified_player_annual',
        'instructor_plus_monthly', 'instructor_plus_annual',
      ]);
      if (PAID_TIERS.includes(queryTier)) {
        const cadenceQs = queryCadence === 'annual' ? '&cadence=annual' : '&cadence=monthly';
        const couponQs = (applyMfmfree3 && MFMFREE3_ELIGIBLE_TIERS.includes(queryTier))
          ? '&coupon=MFMFREE3' : '';
        toast.success(
          couponQs
            ? 'Welcome! MFMFREE3 will auto-apply — finish your subscription to unlock 3 free months.'
            : 'Welcome! Finish your subscription to unlock paid features.'
        );
        navigate(`/subscription?tier=${encodeURIComponent(queryTier)}${cadenceQs}${couponQs}`);
        return;
      }

      // The "Switch to 3-month free trial" upsell on the
      // free-plan card sets this flag. We keep the user on free during
      // signup itself (so they don't get billed), then redirect to
      // /subscription?coupon=MFMFREE3 so they can apply the code in the
      // proper coupon flow without ever hitting PayPal accidentally.
      if (form.selected_plan === 'free' && applyMfmfree3) {
        toast.success('Welcome! Apply MFMFREE3 below to start your 3 free months.');
        navigate('/subscription?coupon=MFMFREE3');
        return;
      }

      // Paid plans run their checkout via PayPal Smart Buttons on the
      // SubscriptionPage (in-app modal — no redirect). Punt the user there
      // with the chosen plan pre-selected so they finish payment in one click.
      if (form.selected_plan !== 'free') {
        const couponQs = (applyMfmfree3 && MFMFREE3_ELIGIBLE_PLAN_IDS.has(form.selected_plan))
          ? '&coupon=MFMFREE3' : '';
        toast.success(
          couponQs
            ? 'Welcome! MFMFREE3 will auto-apply on the next screen.'
            : 'Welcome! Finish your subscription to unlock paid features.'
        );
        navigate(`/subscription?plan=${encodeURIComponent(form.selected_plan)}${couponQs}`);
        return;
      }
      toast.success('Welcome to Meet For Mahjong!');
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0: return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm mb-1.5 block">First Name *</Label>
              <Input value={form.first_name} onChange={e => updateForm('first_name', e.target.value)} className="rounded-xl" data-testid="reg-first-name" />
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Last Name *</Label>
              <Input value={form.last_name} onChange={e => updateForm('last_name', e.target.value)} className="rounded-xl" data-testid="reg-last-name" />
            </div>
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Display Name *</Label>
            <Input value={form.display_name} onChange={e => updateForm('display_name', e.target.value)} placeholder="How other players will see you" className="rounded-xl" data-testid="reg-display-name" />
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Email *</Label>
            <Input
              type="email"
              value={form.email}
              onChange={e => updateForm('email', e.target.value)}
              onPaste={e => {
                // If the user pastes their email, also auto-fill the confirm
                // field — pasting is a high-confidence "this is the email I
                // want" signal, no reason to make them paste twice.
                const pasted = e.clipboardData?.getData('text') || '';
                if (pasted && pasted.includes('@')) {
                  updateForm('confirm_email', pasted.trim());
                }
              }}
              className="rounded-xl"
              data-testid="reg-email"
              autoComplete="email"
            />
            {(() => {
              const sugg = suggestEmailCorrection(form.email);
              if (!sugg) return null;
              return (
                <p className="text-xs text-amber-700 mt-1.5" data-testid="reg-email-typo-suggestion">
                  Did you mean{' '}
                  <button
                    type="button"
                    onClick={() => {
                      updateForm('email', sugg);
                      if (form.confirm_email) updateForm('confirm_email', sugg);
                    }}
                    className="font-medium underline hover:text-amber-900"
                    data-testid="reg-email-typo-apply"
                  >
                    {sugg}
                  </button>?
                </p>
              );
            })()}
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Confirm Email *</Label>
            <Input
              type="email"
              value={form.confirm_email}
              onChange={e => updateForm('confirm_email', e.target.value)}
              onPaste={e => e.preventDefault()}
              className="rounded-xl"
              data-testid="reg-confirm-email"
              placeholder="Re-type your email"
              autoComplete="off"
            />
            <p className="text-xs text-slate-400 mt-1">
              We can't reach you (or recover your account) if your email is wrong, so we ask twice. Pasting is disabled in this field on purpose.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm mb-1.5 block">Password *</Label>
              <Input type="password" value={form.password} onChange={e => updateForm('password', e.target.value)} className="rounded-xl" data-testid="reg-password" />
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Confirm Password *</Label>
              <Input type="password" value={form.confirm_password} onChange={e => updateForm('confirm_password', e.target.value)} className="rounded-xl" data-testid="reg-confirm-password" />
            </div>
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Phone (optional)</Label>
            <Input value={form.phone} onChange={e => updateForm('phone', e.target.value)} placeholder="e.g. 555-123-4567" className="rounded-xl" data-testid="reg-phone" />
            {/* Twilio / CTIA-compliant explicit SMS opt-in. Must be unchecked
                by default, must specifically describe message types, must
                disclose rates + STOP/HELP keywords, and must link to the
                Privacy Policy and Terms. Opting out of SMS is not a barrier
                to creating an account. */}
            <div className={`mt-3 p-3 rounded-xl border ${form.sms_consent ? 'border-[#56dfb4] bg-[#56dfb4]/5' : 'border-slate-200 bg-slate-50/40'}`}>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={form.sms_consent}
                  onCheckedChange={v => updateForm('sms_consent', !!v)}
                  className="mt-0.5"
                  data-testid="reg-sms-consent"
                  disabled={!form.phone || !form.phone.trim()}
                />
                <div className="text-xs text-slate-600 leading-relaxed">
                  <p className="font-medium text-slate-700 mb-1">I agree to receive SMS messages from Meet For Mahjong at the number above.</p>
                  <p>Message types include account updates, event reminders, community notifications, and other site-related messages. Message frequency may vary. <strong>Msg &amp; data rates may apply.</strong> Reply <strong>STOP</strong> to opt out. Reply <strong>HELP</strong> for help. Consent is not a condition of any purchase. See our <Link to="/privacy-policy" target="_blank" className="text-[#56c6df] hover:underline">Privacy Policy</Link> and <Link to="/terms-and-conditions" target="_blank" className="text-[#56c6df] hover:underline">Terms &amp; Conditions</Link>.</p>
                  {(!form.phone || !form.phone.trim()) && (
                    <p className="text-amber-600 mt-1.5">Add a phone number above to enable SMS alerts.</p>
                  )}
                </div>
              </label>
            </div>
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Gender</Label>
            <p className="text-xs text-slate-400 mb-2">We only ask this to help ensure everyone feels safe when finding players. We want this to be an inclusive, safe space for all. If you have a suggestion for how we can word this better, we welcome it on our <a href="/contact" className="text-[#56c6df] hover:underline" target="_blank">Contact page</a>.</p>
            <div className="flex flex-wrap gap-2">
              {[{v:'female',l:'Female'},{v:'male',l:'Male'},{v:'non_binary',l:'Non-binary'},{v:'prefer_not_to_say',l:'Prefer not to say'}].map(g => (
                <button key={g.v} type="button" onClick={() => updateForm('gender', g.v)}
                  className={`px-4 py-2 rounded-full text-sm border transition-all ${form.gender === g.v ? 'bg-[#56c6df] text-white border-[#56c6df]' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid={`reg-gender-${g.v}`}>{g.l}</button>
              ))}
            </div>
          </div>
        </div>
      );
      case 1: return (
        <div className="space-y-6">
          <div>
            <Label className="text-sm font-medium mb-3 block">Skill Level *</Label>
            <RadioGroup value={form.skill_level} onValueChange={v => updateForm('skill_level', v)} className="space-y-2">
              {SKILL_LEVELS.map(s => (
                <label key={s.value} className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${form.skill_level === s.value ? 'border-[#56dfb4] bg-[#56dfb4]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                  <RadioGroupItem value={s.value} data-testid={`reg-skill-${s.value}`} />
                  <div><p className="font-medium text-sm">{s.label}</p><p className="text-xs text-slate-500">{s.desc}</p></div>
                </label>
              ))}
            </RadioGroup>
          </div>
          <div>
            <Label className="text-sm font-medium mb-3 block">Do you have a Mahjong set? *</Label>
            <RadioGroup value={form.has_mahjong_set} onValueChange={v => updateForm('has_mahjong_set', v)} className="space-y-2">
              {SET_OPTIONS.map(s => (
                <label key={s.value} className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${form.has_mahjong_set === s.value ? 'border-[#56dfb4] bg-[#56dfb4]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                  <RadioGroupItem value={s.value} data-testid={`reg-set-${s.value}`} /><span className="text-sm">{s.label}</span>
                </label>
              ))}
            </RadioGroup>
          </div>
          <div className="border-t border-slate-100 pt-5 mt-2">
            <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border border-slate-200 hover:border-[#b456df] transition-all">
              <Checkbox checked={form.is_instructor} onCheckedChange={v => {
                updateForm('is_instructor', v);
                // Toggling instructor on/off resets the plan selection so
                // they land on the appropriate "free" tier (Open Play vs
                // Instructor Starter). MFMFREE3 stays available either
                // way — it applies to Verified Player AND Instructor
                // Advantage.
                setShowPlayerPlansForInstructor(false);
                updateForm('selected_plan', 'free');
              }} className="mt-0.5" data-testid="reg-instructor-checkbox" />
              <div>
                <p className="font-medium text-sm">I teach Mahjong</p>
                <p className="text-xs text-slate-500">Check this if you offer group or private Mahjong lessons</p>
              </div>
            </label>
            {form.is_instructor && (
              <div className="mt-3 space-y-3 pl-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={form.instructor_public} onCheckedChange={v => updateForm('instructor_public', v)} data-testid="reg-instructor-public" />
                  <span className="text-sm">List me publicly as an instructor</span>
                </label>
                <div>
                  <Label className="text-xs mb-1 block">Brief bio about your teaching</Label>
                  <Input value={form.instructor_bio} onChange={e => updateForm('instructor_bio', e.target.value)} placeholder="e.g. Teaching beginners for 5 years..." className="rounded-xl text-sm" data-testid="reg-instructor-bio" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Lesson types offered</Label>
                  <div className="flex flex-wrap gap-2">
                    {['group', 'private', 'online', 'in-person'].map(t => (
                      <button key={t} type="button" onClick={() => toggleArray('instructor_lesson_types', t)}
                        className={`px-3 py-1 rounded-full text-xs border transition-all capitalize ${form.instructor_lesson_types.includes(t) ? 'bg-[#b456df] text-white border-[#b456df]' : 'border-slate-200'}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      );
      case 2: return (
        <div className="space-y-6">
          <div>
            <Label className="text-sm font-medium mb-3 block">Meetup type preference *</Label>
            <RadioGroup value={form.meetup_type} onValueChange={v => updateForm('meetup_type', v)} className="space-y-2">
              {MEETUP_TYPES.map(s => (
                <label key={s.value} className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${form.meetup_type === s.value ? 'border-[#56dfb4] bg-[#56dfb4]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                  <RadioGroupItem value={s.value} data-testid={`reg-meetup-${s.value}`} />
                  <div><p className="font-medium text-sm">{s.label}</p><p className="text-xs text-slate-500">{s.desc}</p></div>
                </label>
              ))}
            </RadioGroup>
          </div>
          <div>
            <Label className="text-sm font-medium mb-3 block">Gender preference *</Label>
            <RadioGroup value={form.gender_preference} onValueChange={v => updateForm('gender_preference', v)} className="space-y-2">
              {GENDER_PREFS.map(s => (
                <label key={s.value} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${form.gender_preference === s.value ? 'border-[#56dfb4] bg-[#56dfb4]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                  <RadioGroupItem value={s.value} data-testid={`reg-gender-${s.value}`} /><span className="text-sm">{s.label}</span>
                </label>
              ))}
            </RadioGroup>
          </div>
          <div>
            <Label className="text-sm font-medium mb-3 block">Travel distance *</Label>
            <div className="flex flex-wrap gap-2">
              {DISTANCES.map(d => (
                <button key={d} type="button" onClick={() => updateForm('travel_distance', d)}
                  className={`px-4 py-2 rounded-full text-sm border transition-all ${form.travel_distance === d ? 'bg-[#56dfb4] text-slate-900 border-[#56dfb4]' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid={`reg-distance-${d}`}>
                  {d === '50+' ? '50+ miles' : `${d} miles`}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
      case 3: return (
        <div className="space-y-6">
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Zip Code (US only) *</Label>
            <Input value={form.zip_code} onChange={e => updateForm('zip_code', e.target.value)} placeholder="e.g. 10001" maxLength={5} className="rounded-xl max-w-xs" data-testid="reg-zip-code" />
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block">When are you available to play?</Label>
            <p className="text-xs text-slate-400 mb-3">Select days you're available, then choose specific times for each day.</p>
            <div className="space-y-2">
              {DAYS.map(d => {
                const selected = form.days_available.includes(d);
                const dayTimes = form.day_times[d] || [];
                return (
                  <div key={d} className={`rounded-xl border transition-all ${selected ? 'border-[#56c6df] bg-[#56c6df]/5' : 'border-slate-200'}`}>
                    <button type="button" onClick={() => {
                      toggleArray('days_available', d);
                      if (selected) { updateForm('day_times', {...form.day_times, [d]: undefined}); }
                    }} className="w-full flex items-center gap-3 p-3 text-left" data-testid={`reg-day-${d}`}>
                      <Checkbox checked={selected} className="pointer-events-none" />
                      <span className="text-sm font-medium capitalize">{d}</span>
                    </button>
                    {selected && (
                      <div className="px-3 pb-3 flex flex-wrap gap-1.5 ml-8">
                        {TIMES.map(t => (
                          <button key={t.value} type="button" onClick={() => {
                            const cur = form.day_times[d] || [];
                            const next = cur.includes(t.value) ? cur.filter(v => v !== t.value) : [...cur, t.value];
                            updateForm('day_times', {...form.day_times, [d]: next});
                          }}
                            className={`px-2.5 py-1 rounded-full text-xs border transition-all ${(dayTimes).includes(t.value) ? 'bg-[#56c6df] text-white border-[#56c6df]' : 'border-slate-200 hover:border-slate-300'}`}
                            data-testid={`reg-daytime-${d}-${t.value}`}>
                            {t.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
      case 4: return (
        <div className="space-y-6">
          <div>
            <Label className="text-sm font-medium mb-3 block">Alert Preferences</Label>
            <div className="space-y-2">
              {ALERTS.map(a => (
                <label key={a.value} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${form.alert_preferences.includes(a.value) ? 'border-[#56dfb4] bg-[#56dfb4]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                  <Checkbox checked={form.alert_preferences.includes(a.value)} onCheckedChange={() => toggleArray('alert_preferences', a.value)} data-testid={`reg-alert-${a.value}`} />
                  <span className="text-sm">{a.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="p-4 rounded-xl bg-[#56dfb4]/5 border border-[#56dfb4]/20">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={form.last_minute_alerts} onCheckedChange={v => updateForm('last_minute_alerts', v)} className="mt-0.5" data-testid="reg-last-minute-alerts" />
              <div>
                <p className="font-medium text-sm">Alert me for last-minute games</p>
                <p className="text-xs text-slate-500">Get notified when a player near you needs someone for a last-minute game. {form.sms_consent && form.phone ? 'Sent via email and SMS.' : 'Sent via email.'}</p>
              </div>
            </label>
          </div>
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={form.agreed_to_terms} onCheckedChange={v => updateForm('agreed_to_terms', v)} className="mt-0.5" data-testid="reg-terms-checkbox" />
              <span className="text-sm text-slate-600">
                I agree to the <Link to="/terms-and-conditions" className="text-[#df5681] hover:underline" target="_blank">Terms &amp; Conditions</Link> and{' '}
                <Link to="/privacy-policy" className="text-[#df5681] hover:underline" target="_blank">Privacy Policy</Link>, and I will follow the community guidelines.
              </span>
            </label>
          </div>
          <div className="border-t border-slate-100 pt-5 mt-2">
            <Label className="text-sm font-medium mb-3 block">Social Media (optional, helps others verify your identity)</Label>
            <div className="space-y-2">
              <Input value={form.social_facebook} onChange={e => updateForm('social_facebook', e.target.value)} placeholder="Facebook profile URL" className="rounded-xl text-sm" data-testid="reg-social-facebook" />
              <Input value={form.social_instagram} onChange={e => updateForm('social_instagram', e.target.value)} placeholder="Instagram handle" className="rounded-xl text-sm" data-testid="reg-social-instagram" />
              <Input value={form.social_twitter} onChange={e => updateForm('social_twitter', e.target.value)} placeholder="X (Twitter) handle" className="rounded-xl text-sm" data-testid="reg-social-twitter" />
              <Input value={form.social_linkedin} onChange={e => updateForm('social_linkedin', e.target.value)} placeholder="LinkedIn profile URL" className="rounded-xl text-sm" data-testid="reg-social-linkedin" />
            </div>
          </div>
          <div className="border-t border-slate-100 pt-5 mt-2">
            <Label className="text-sm font-medium mb-1.5 block">Referral Code (optional)</Label>
            <Input value={form.referral_code} onChange={e => updateForm('referral_code', e.target.value.toUpperCase())} placeholder="Enter a friend's referral code" className="rounded-xl max-w-xs uppercase" data-testid="reg-referral-code" />
            <p className="text-xs text-slate-400 mt-1">When your friend upgrades to Verified Player, you both get 1 free month!</p>
          </div>
        </div>
      );
      case 5: {
        const isInstructor = !!form.is_instructor;
        return (
        <div className="space-y-4">
          {isInstructor ? (
            <>
              <div className="p-4 rounded-2xl bg-[#b456df]/5 border border-[#b456df]/20" data-testid="reg-instructor-plan-intro">
                <p className="text-sm font-medium text-slate-700">
                  <span className="mr-1.5" aria-hidden="true">🎓</span>
                  You marked yourself as an instructor — here are the plans built for teachers.
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Instructor plans include event posting, the Premium Instructor badge, and your public instructor profile. Player plans don't include these.
                </p>
              </div>

              {/* MFMFREE3 hero for instructors. MFMFREE3 applies to Verified
                  Player AND Instructor Advantage — surface it here so
                  instructor signups also see the 3-months-free trial as
                  the path of least resistance. Selecting it keeps
                  selected_plan='free' (Instructor Starter on signup) and
                  applyMfmfree3=true; the post-signup redirect lands them
                  on /subscription?coupon=MFMFREE3 where they can apply
                  the code to Instructor Advantage. Hides automatically
                  once the promo retires (see lib/promoConfig.js). */}
              {isPromoActive() && (
              <div
                className={`p-6 rounded-2xl border-2 transition-all ${
                  form.selected_plan === 'free' && applyMfmfree3
                    ? 'border-[#b456df] bg-gradient-to-br from-[#b456df]/15 via-[#df5681]/10 to-white shadow-md'
                    : 'border-slate-200'
                }`}
                data-testid="reg-instructor-mfmfree3-hero"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#b456df]/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl" aria-hidden="true">🎁</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-0.5 bg-[#df5681] text-white rounded-full text-xs font-bold uppercase tracking-wider">Recommended</span>
                      <span className="px-2 py-0.5 bg-slate-900 text-white rounded-full text-xs font-bold">No Card Required</span>
                    </div>
                    <h3 className="text-xl font-bold mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      3 Months Free Instructor Advantage
                    </h3>
                    <p className="text-sm text-slate-600 mt-1">
                      Code <strong className="text-[#df5681]">MFMFREE3</strong> works on Instructor Advantage too — free event posting, discounted paid events, and the Premium Instructor badge for 3 months. Cancel anytime — no auto-charge while you're in the trial.
                    </p>
                    <div className="flex items-center gap-2 mt-4">
                      <Button
                        type="button"
                        onClick={() => {
                          updateForm('selected_plan', 'free');
                          setApplyMfmfree3(true);
                          setShowPlayerPlansForInstructor(false);
                        }}
                        className={`rounded-full font-semibold ${
                          form.selected_plan === 'free' && applyMfmfree3
                            ? 'bg-[#b456df] text-white hover:bg-[#9744bc]'
                            : 'bg-[#df5681] text-white hover:bg-[#c44570]'
                        }`}
                        data-testid="reg-instructor-mfmfree3-hero-btn"
                      >
                        {form.selected_plan === 'free' && applyMfmfree3 ? '✓ Free trial selected' : 'Claim my 3 free months'}
                      </Button>
                      <span className="text-xs text-slate-400">Applied right after you sign up</span>
                    </div>
                  </div>
                </div>
              </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="reg-instructor-plans-grid">
                {INSTRUCTOR_PLANS.map(p => (
                  <button key={p.id} type="button"
                    onClick={() => {
                      updateForm('selected_plan', p.id);
                      // Picking an instructor plan card explicitly opts
                      // out of the MFMFREE3 trial hero so the user goes
                      // straight to paid checkout (paid plans) or to
                      // dashboard (Instructor Starter without the trial).
                      setApplyMfmfree3(false);
                    }}
                    className={`p-5 rounded-2xl border-2 text-left transition-all relative ${
                      form.selected_plan === p.id && !(p.id === 'free' && applyMfmfree3) ? 'border-[#b456df] shadow-md' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`reg-instructor-plan-${p.id}`}>
                    {p.best && <span className="absolute -top-2.5 right-3 px-2 py-0.5 bg-[#b456df] text-white rounded-full text-xs font-bold">Best Value</span>}
                    {form.selected_plan === p.id && !(p.id === 'free' && applyMfmfree3) && <div className="absolute top-3 right-3 w-5 h-5 bg-[#b456df] rounded-full flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                    <h4 className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.name}</h4>
                    <p className="mt-1"><span className="text-2xl font-bold">{p.price}</span><span className="text-sm text-slate-500 ml-1">{p.period}</span></p>
                  </button>
                ))}
              </div>

              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={() => setShowPlayerPlansForInstructor(!showPlayerPlansForInstructor)}
                  className="text-sm text-slate-500 hover:text-slate-700 underline underline-offset-2"
                  data-testid="reg-instructor-show-player-plans"
                >
                  {showPlayerPlansForInstructor ? 'Hide player plans' : 'See player plans instead →'}
                </button>
              </div>

              {showPlayerPlansForInstructor && (
                <div className="pt-2">
                  <p className="text-xs text-amber-600 mb-2">
                    ⚠️ Player plans don't include instructor features like event posting or the Premium Instructor badge.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="reg-instructor-player-plans-grid">
                    {PLANS.map(p => (
                      <button key={p.id} type="button"
                        onClick={() => {
                          // Instructor picking a player plan → confirm modal
                          setPendingPlayerPlan(p.id);
                          setInstructorMismatchOpen(true);
                        }}
                        className={`p-5 rounded-2xl border-2 text-left transition-all relative ${
                          form.selected_plan === p.id && PLAYER_PLAN_IDS.has(p.id) ? 'border-[#56dfb4] shadow-md' : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`reg-instructor-player-plan-${p.id}`}>
                        {p.best && <span className="absolute -top-2.5 right-3 px-2 py-0.5 bg-[#56dfb4] text-slate-900 rounded-full text-xs font-bold">Best Value</span>}
                        <h4 className="font-semibold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.name}</h4>
                        <p className="mt-1"><span className="text-xl font-bold">{p.price}</span><span className="text-xs text-slate-500 ml-1">{p.period}</span></p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Instructor mismatch confirmation modal */}
              {instructorMismatchOpen && (
                <div
                  className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
                  onClick={() => { setInstructorMismatchOpen(false); setPendingPlayerPlan(null); }}
                  data-testid="reg-instructor-mismatch-overlay"
                >
                  <div
                    className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl"
                    onClick={e => e.stopPropagation()}
                    data-testid="reg-instructor-mismatch-modal"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-2xl bg-[#b456df]/15 flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl" aria-hidden="true">🎓</span>
                      </div>
                      <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Are you sure?</h3>
                    </div>
                    <p className="text-sm text-slate-600 mb-5">
                      You said you teach Mahjong, but{' '}
                      <strong>{(PLANS.find(p => p.id === pendingPlayerPlan)?.name) || 'this plan'}</strong>{' '}
                      is a <strong>player-only plan</strong>. It does <strong>not</strong> include event posting, your public instructor profile, or the Premium Instructor badge. To teach on Meet For Mahjong, you'll want an instructor plan.
                    </p>
                    <div className="flex flex-col gap-2">
                      <Button
                        type="button"
                        onClick={() => {
                          // Stay on instructor plans — keep selection on instructor_starter (free)
                          updateForm('selected_plan', 'free');
                          setInstructorMismatchOpen(false);
                          setPendingPlayerPlan(null);
                          setShowPlayerPlansForInstructor(false);
                        }}
                        className="w-full rounded-full bg-[#b456df] text-white hover:bg-[#9744bc] font-semibold"
                        data-testid="reg-instructor-mismatch-stay-btn"
                      >
                        Keep me on an instructor plan
                      </Button>
                      <button
                        type="button"
                        onClick={() => {
                          // User insists — commit the player plan they picked
                          if (pendingPlayerPlan) {
                            updateForm('selected_plan', pendingPlayerPlan);
                            if (pendingPlayerPlan !== 'free') setApplyMfmfree3(false);
                          }
                          setInstructorMismatchOpen(false);
                          setPendingPlayerPlan(null);
                        }}
                        className="w-full text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2 py-2"
                        data-testid="reg-instructor-mismatch-continue-btn"
                      >
                        Continue with player plan anyway
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
          {isPromoActive() && (
            <p className="text-sm text-slate-500 mb-4">Your 3-month free trial is ready — just confirm below. You can change your plan anytime.</p>
          )}

          {/* HERO CARD — preselected 3-month free trial (MFMFREE3).
              This is the "one big decision" that replaces the old 4-card
              grid. Signups were drifting to the Free plan because the
              upsell was behind a secondary click; now the free trial is
              the default and anything else requires an explicit toggle.
              Hides automatically once the promo retires; the plans grid
              below opens by default in that case. */}
          {isPromoActive() && (
          <div
            className={`p-6 rounded-2xl border-2 transition-all ${
              form.selected_plan === 'free' && applyMfmfree3
                ? 'border-[#56dfb4] bg-gradient-to-br from-[#56dfb4]/15 via-[#56c6df]/10 to-white shadow-md'
                : 'border-slate-200'
            }`}
            data-testid="reg-mfmfree3-hero"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-[#56dfb4]/20 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl" aria-hidden="true">🎁</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2 py-0.5 bg-[#df5681] text-white rounded-full text-xs font-bold uppercase tracking-wider">Recommended</span>
                  <span className="px-2 py-0.5 bg-slate-900 text-white rounded-full text-xs font-bold">No Card Required</span>
                </div>
                <h3 className="text-xl font-bold mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  3 Months Free Verified Player
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Unlock events, messaging, the discussion board, and the full player directory for free with code{' '}
                  <strong className="text-[#df5681]">MFMFREE3</strong>. Stays free for 3 months. Cancel anytime — no auto-charge while you're in the trial.
                </p>
                <div className="flex items-center gap-2 mt-4">
                  <Button
                    type="button"
                    onClick={() => {
                      updateForm('selected_plan', 'free');
                      setApplyMfmfree3(true);
                      setShowAllPlans(false);
                    }}
                    className={`rounded-full font-semibold ${
                      form.selected_plan === 'free' && applyMfmfree3
                        ? 'bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b]'
                        : 'bg-[#df5681] text-white hover:bg-[#c44570]'
                    }`}
                    data-testid="reg-mfmfree3-hero-btn"
                  >
                    {form.selected_plan === 'free' && applyMfmfree3 ? '✓ Free trial selected' : 'Claim my 3 free months'}
                  </Button>
                  <span className="text-xs text-slate-400">Applied right after you sign up</span>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Small "see all plans" toggle. Keeps the default decision
              simple but doesn't hide paid plans from people who WANT to
              pay for lifetime etc. Hidden once the promo retires — at
              that point the plans grid is the only path so a collapse
              toggle would just be noise. */}
          {isPromoActive() && (
          <div className="text-center pt-1">
            <button
              type="button"
              onClick={() => setShowAllPlans(v => !v)}
              className="text-sm text-slate-500 hover:text-slate-700 underline underline-offset-2"
              data-testid="reg-see-all-plans-toggle"
            >
              {showAllPlans ? 'Hide other plans' : 'See all plans →'}
            </button>
          </div>
          )}

          {showAllPlans && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2" data-testid="reg-plans-grid">
              {PLANS.map(p => (
                <button key={p.id} type="button"
                  onClick={() => {
                    // If the user actively picks "Free" (and was on the MFMFREE3
                    // trial), trigger the exit-intent modal before letting them
                    // commit — one last friendly nudge not to skip the free months.
                    if (p.id === 'free' && applyMfmfree3) {
                      setFreeTrapOpen(true);
                      return;
                    }
                    updateForm('selected_plan', p.id);
                    if (p.id !== 'free') setApplyMfmfree3(false);
                  }}
                  className={`p-5 rounded-2xl border-2 text-left transition-all relative ${
                    form.selected_plan === p.id && !(p.id === 'free' && applyMfmfree3)
                      ? 'border-[#56dfb4] shadow-md'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`reg-plan-${p.id}`}>
                  {p.best && <span className="absolute -top-2.5 right-3 px-2 py-0.5 bg-[#56dfb4] text-slate-900 rounded-full text-xs font-bold">Best Value</span>}
                  {form.selected_plan === p.id && !(p.id === 'free' && applyMfmfree3) && <div className="absolute top-3 right-3 w-5 h-5 bg-[#56dfb4] rounded-full flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                  <h4 className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.name}</h4>
                  <p className="mt-1"><span className="text-2xl font-bold">{p.price}</span><span className="text-sm text-slate-500 ml-1">{p.period}</span></p>
                </button>
              ))}
            </div>
          )}

          {/* Exit-intent modal — fires when someone clicks Free despite the
              trial being preselected. One last friendly nudge. */}
          {freeTrapOpen && (
            <div
              className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setFreeTrapOpen(false)}
              data-testid="reg-free-trap-overlay"
            >
              <div
                className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl"
                onClick={e => e.stopPropagation()}
                data-testid="reg-free-trap-modal"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-2xl bg-[#df5681]/15 flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl" aria-hidden="true">👋</span>
                  </div>
                  <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Before you pick Free…</h3>
                </div>
                <p className="text-sm text-slate-600 mb-5">
                  You could get <strong>3 months of Verified Player completely free</strong> with code{' '}
                  <strong className="text-[#df5681]">MFMFREE3</strong>. No credit card required during the trial, and you can cancel anytime. Open Play members have limited access — Verified Player unlocks events, messaging, and the full player directory.
                </p>
                <div className="flex flex-col gap-2">
                  <Button
                    type="button"
                    onClick={() => {
                      updateForm('selected_plan', 'free');
                      setApplyMfmfree3(true);
                      setShowAllPlans(false);
                      setFreeTrapOpen(false);
                    }}
                    className="w-full rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] font-semibold"
                    data-testid="reg-free-trap-accept-btn"
                  >
                    Yes, claim my 3 free months
                  </Button>
                  <button
                    type="button"
                    onClick={() => {
                      updateForm('selected_plan', 'free');
                      setApplyMfmfree3(false);
                      setFreeTrapOpen(false);
                    }}
                    className="w-full text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2 py-2"
                    data-testid="reg-free-trap-decline-btn"
                  >
                    No thanks, continue with the limited free plan
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Social follow nudge on the final registration step. Catches every
              new member right before they submit, highest-intent moment for a
              free follow. */}
          <div className="mt-6 p-4 rounded-2xl bg-[#df5681]/5 border border-[#df5681]/20">
            <p className="text-sm font-medium text-slate-700 mb-2">
              <span className="mr-1.5" aria-hidden="true">💬</span>
              One more thing — stay connected!
            </p>
            <p className="text-xs text-slate-500 mb-3">
              Follow Meet For Mahjong for community updates, event highlights, and news.
            </p>
            <div className="flex flex-wrap gap-2">
              <a
                href="https://www.facebook.com/meetformahjong"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-medium text-slate-700 hover:border-[#1877F2] hover:text-[#1877F2] transition-colors"
                data-testid="reg-follow-facebook"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"/>
                </svg>
                Follow on Facebook
              </a>
              <a
                href="https://www.instagram.com/meetformahjong"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-medium text-slate-700 hover:border-[#E4405F] hover:text-[#E4405F] transition-colors"
                data-testid="reg-follow-instagram"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                </svg>
                Follow on Instagram
              </a>
            </div>
          </div>
            </>
          )}

          {/* Terms agreement — also lives at the end of Step 4, but mirrored
             here so users who reach Step 5 without checking it (or who scrolled
             past it accidentally) can agree right before submitting instead of
             backtracking. Same form state — checking either checkbox flips
             `agreed_to_terms` to true. */}
          <div
            className={`p-4 rounded-xl border ${form.agreed_to_terms ? 'bg-[#56dfb4]/5 border-[#56dfb4]/30' : 'bg-amber-50 border-amber-200'}`}
            data-testid="reg-terms-checkbox-plan-step-wrapper"
          >
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={form.agreed_to_terms}
                onCheckedChange={v => updateForm('agreed_to_terms', v)}
                className="mt-0.5"
                data-testid="reg-terms-checkbox-plan-step"
              />
              <span className="text-sm text-slate-700">
                I agree to the <Link to="/terms-and-conditions" className="text-[#df5681] hover:underline" target="_blank">Terms &amp; Conditions</Link> and{' '}
                <Link to="/privacy-policy" className="text-[#df5681] hover:underline" target="_blank">Privacy Policy</Link>, and I will follow the community guidelines.
                {!form.agreed_to_terms && (
                  <span className="block text-xs text-amber-700 font-medium mt-1">
                    Required — check this box to continue.
                  </span>
                )}
              </span>
            </label>
          </div>
        </div>
      );
      }
      default: return null;
    }
  };

  return (
    OUTAGE_MODE ? <OutageNotice context="register" /> :
    // `translate="no"` + `notranslate` class disables Google Translate,
    // Chrome auto-translate, Grammarly, DarkReader, and similar
    // extensions that mutate the DOM under React's feet. Without it,
    // this multi-step form crashes with "Failed to execute 'removeChild'
    // on 'Node'" when a translator replaces a button's text between
    // step transitions. Caught 03 Jul 2026 in production error report.
    <div
      data-testid="register-page"
      className="min-h-[80vh] py-12 px-4 notranslate"
      translate="no"
      lang="en"
    >
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Create Your Account</h1>
          <p className="text-slate-500 mt-2">Step {step + 1} of {STEPS.length}: {STEPS[step]}</p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <Progress value={((step + 1) / STEPS.length) * 100} className="h-2 rounded-full" data-testid="register-progress" />
          <div className="flex justify-between mt-2">
            {STEPS.map((s, i) => (
              <span key={s} className={`text-xs ${i <= step ? 'text-[#56dfb4] font-medium' : 'text-slate-400'}`}>
                {i < step ? <Check className="w-3 h-3 inline" /> : null} {i + 1}
              </span>
            ))}
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm">
          {error && <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-600 text-sm" data-testid="register-error">{error}</div>}

          {renderStep()}

          <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
            {step > 0 ? (
              <Button variant="ghost" onClick={prevStep} className="rounded-full gap-1" data-testid="reg-prev-btn">
                <ArrowLeft className="w-4 h-4" /> Back
              </Button>
            ) : (
              <div />
            )}
            {step < 5 ? (
              <Button onClick={nextStep} className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] gap-1 px-6" data-testid="reg-next-btn">
                Next <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button onClick={handleRegister} disabled={loading} className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] px-8" data-testid="reg-submit-btn">
                {loading ? 'Creating Account...' : form.selected_plan === 'free' ? 'Create Account' : 'Create Account & Pay'}
              </Button>
            )}
          </div>
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          Already have an account? <Link to="/login" className="text-[#df5681] hover:underline font-medium">Sign In</Link>
        </p>
      </div>
    </div>
  );
}
